import { type ApolloClient } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { reorder } from '@atlaskit/pragmatic-drag-and-drop/reorder';
import type {
	ReorderGroupItemsMutation,
	ReorderGroupItemsMutationVariables,
	PatchProjectGroupMutation,
	PatchProjectGroupMutationVariables,
	DeleteProjectItemsMutation,
	DeleteProjectItemsMutationVariables,
	DeleteProjectSubItemsMutation,
	DeleteProjectSubItemsMutationVariables,
	AddProductToProjectsMutation,
	AddProductToProjectsMutationVariables,
	AddProductsToProjectsMutation,
	AddProductsToProjectsMutationVariables,
	ProjectDetailsGroupFieldsFragment,
	ChangeCartItemFinishMutation,
	ChangeCartItemFinishMutationVariables,
	CreateOrUpdateCustomProductMutation,
	CreateOrUpdateCustomProductMutationVariables
} from '../../__generated__/graphql-client-types';
import { modifyPendingItems } from '../../helpers/drag-and-drop/projects-drag-and-drop.helpers';
import { ADD_PRODUCTS_TO_PROJECTS } from '../../queries/project/batch-add-to-projects.queries';
import {
	REORDER_GROUP_ITEMS,
	PATCH_PROJECT_GROUP,
	DELETE_PROJECT_ITEMS,
	DELETE_PROJECT_SUBITEMS,
	PROJECT_DETAILS_PAGE,
	CHANGE_CART_ITEM_FINISH,
	CREATE_OR_UPDATE_CUSTOM_PRODUCT
} from '../../queries/project/project-details.queries';
import { ADD_PRODUCT_TO_PROJECTS } from '../../queries/project/project.queries';
import type { MoveToPosition, ProjectDetailsGroup, ProductUpdatePayload, RemovedProjectItem } from '../../types/project.types';
import { useProjectDetails, useProjectId } from './project-details.hooks';

// Keep the fallback reference stable so effects do not dispatch on every render when project data is unavailable.
const EMPTY_PROJECT_GROUPS: ProjectDetailsGroup[] = [];

export const useProjectGroups = (): ProjectDetailsGroup[] => {
	const { project } = useProjectDetails();
	return project?.groups ?? EMPTY_PROJECT_GROUPS;
};

export const useProjectGroupMutations = () => {
	const projectId = useProjectId();
	const groups = useProjectGroups();
	const { project } = useProjectDetails();
	const [reorderGroupItems] = useMutation<ReorderGroupItemsMutation, ReorderGroupItemsMutationVariables>(REORDER_GROUP_ITEMS);
	const [patchProjectGroup] = useMutation<PatchProjectGroupMutation, PatchProjectGroupMutationVariables>(PATCH_PROJECT_GROUP);
	const [deleteProjectItems] = useMutation<DeleteProjectItemsMutation, DeleteProjectItemsMutationVariables>(DELETE_PROJECT_ITEMS);
	const [deleteProjectSubItem] = useMutation<DeleteProjectSubItemsMutation, DeleteProjectSubItemsMutationVariables>(
		DELETE_PROJECT_SUBITEMS
	);
	const [addProductToProjects] = useMutation<AddProductToProjectsMutation, AddProductToProjectsMutationVariables>(
		ADD_PRODUCT_TO_PROJECTS
	);
	const [addProductsToProjectsBatch] = useMutation<AddProductsToProjectsMutation, AddProductsToProjectsMutationVariables>(
		ADD_PRODUCTS_TO_PROJECTS
	);
	const [changeCartItemFinish, { loading: isUpdatingProductOptions }] = useMutation<
		ChangeCartItemFinishMutation,
		ChangeCartItemFinishMutationVariables
	>(CHANGE_CART_ITEM_FINISH);
	const [createOrUpdateCustomProduct] = useMutation<CreateOrUpdateCustomProductMutation, CreateOrUpdateCustomProductMutationVariables>(
		CREATE_OR_UPDATE_CUSTOM_PRODUCT,
		{ refetchQueries: [{ query: PROJECT_DETAILS_PAGE, variables: { projectId } }], awaitRefetchQueries: true }
	);

	const moveGroup = async (groupId: number, ordinal: number) => {
		const groupName = groups.find((g) => g.id === groupId)?.name;
		await patchProjectGroup({ variables: { input: { projectId, groupId, displayOrder: ordinal, name: groupName } } });
	};

	const reorderItem = async (groupId: number, targetOrdinal: number, itemIds: number[]) => {
		await reorderGroupItems({ variables: { input: { projectId, groupId, targetOrdinal, itemIds } } });
	};

	const getFinishIndex = (index: number, numGroups: number, position: MoveToPosition): number => {
		switch (position) {
			case 'up':
				return index - 1;
			case 'down':
				return index + 1;
			case 'top':
				return 0;
			case 'bottom':
				return numGroups - 1;
		}
	};

	const moveGroupPosition = async (
		index: number,
		position: MoveToPosition,
		updateGroupState: (projectGroups: ProjectDetailsGroupFieldsFragment[]) => void,
		disableDrag: (isDisabled: boolean) => void
	) => {
		disableDrag(true);
		const finishIndex = getFinishIndex(index, groups.length, position);
		const reorderedGroups = reorder({
			list: groups,
			startIndex: index,
			finishIndex
		});

		updateGroupState(reorderedGroups);

		try {
			await moveGroup(groups[index].id, finishIndex + 1);
		} finally {
			disableDrag(false);
		}
	};

	const moveItemPosition = async (
		itemId: number,
		groupId: number,
		itemIndex: number,
		position: MoveToPosition,
		updateGroupState: (projectGroups: ProjectDetailsGroupFieldsFragment[]) => void,
		disableDrag: (isDisabled: boolean) => void
	) => {
		disableDrag(true);
		const groupsCopy = [...groups];
		const groupIndex = groups.findIndex((group) => group.id === groupId);
		const pendingItems = groups[groupIndex].pending ?? [];
		const finishIndex = getFinishIndex(itemIndex, pendingItems.length, position);
		const reorderedItems = reorder({
			list: pendingItems,
			startIndex: itemIndex,
			finishIndex
		});

		const newGroups = modifyPendingItems(groupsCopy, groupIndex, reorderedItems);

		updateGroupState(newGroups);

		try {
			await reorderItem(groupId, finishIndex, [itemId]);
		} finally {
			disableDrag(false);
		}
	};

	return {
		moveGroupPosition,
		moveGroup,
		moveItemPosition,
		reorderItem,
		isUpdatingProductOptions,
		updateGroupName: (groupId: number, name: string) => {
			void patchProjectGroup({ variables: { input: { projectId, groupId, name } } });
		},
		deleteGroupItem: async (itemId: number, groupId: number) => {
			const cartId = groups.find((group) => group.id === groupId)?.cartId;
			await deleteProjectItems({
				variables: {
					input: {
						projectId,
						items: [
							{
								cartId: Number(cartId),
								itemId
							}
						]
					}
				}
			});
		},
		deleteSubItem: async (itemId: number, groupId: number, subItemId: number, isSingleCartProject: boolean) => {
			const cartId = groups.find((group) => group.id === groupId)?.cartId;
			await deleteProjectSubItem({
				variables: {
					input: {
						projectId,
						isSingleCartProject,
						groupId,
						items: [
							{
								cartId: Number(cartId),
								itemId,
								subItemId
							}
						]
					}
				}
			});
		},
		addProductToProject: (
			groupId: number,
			variantId: number,
			quantity: number,
			parentItemId?: number,
			ordinal?: number,
			subItems?: { productVariantId: number; quantityUnPurchased: number | null; options?: { id: number }[] | null }[],
			pricedOptions?: { pricedOptionId: number }[]
		): Promise<ApolloClient.MutateResult<AddProductToProjectsMutation>> => {
			const cartId = groups.find((group) => group.id === groupId)?.cartId;
			return addProductToProjects({
				variables: {
					input: {
						projects: [
							{
								cartId,
								projectId,
								quantity,
								groupId: project?.cartId ? groupId : cartId
							}
						],
						addToCartInput: {
							pricedOptions: pricedOptions ?? [],
							subItems:
								subItems?.map((subItem, idx) => ({
									variantId: subItem.productVariantId,
									quantity: subItem.quantityUnPurchased || 1,
									ordinal: idx + 1,
									pricedOptions: subItem.options?.map((opt) => ({ pricedOptionId: opt.id })) ?? []
								})) ?? [],
							variantId,
							parentItemId,
							ordinal
						}
					}
				},
				refetchQueries: [{ query: PROJECT_DETAILS_PAGE, variables: { projectId } }]
			});
		},
		deleteItem: (itemId: number, groupId: number) => {
			const cartId = groups.find((group) => group.id === groupId)?.cartId;
			void deleteProjectItems({
				variables: {
					input: {
						projectId,
						items: [
							{
								cartId: Number(cartId),
								itemId
							}
						]
					}
				}
			});
		},
		addCustomProductToProject: ({
			groupId,
			cartId,
			isSingleCartProject,
			item,
			quantity,
			ordinal
		}: {
			groupId: number;
			cartId: number;
			isSingleCartProject: boolean;
			item: {
				productId: string;
				title: string;
				unitPrice?: number | null;
				manufacturer?: string | null;
				finish?: string | null;
				image?: { photoId?: number | null } | null;
				specSheet?: { specSheetId?: number | null; specSheetName?: string | null } | null;
			};
			quantity: number;
			ordinal?: number;
		}) => {
			return createOrUpdateCustomProduct({
				variables: {
					input: {
						cartIds: [cartId],
						isSingleCartProject,
						projectId,
						groupId,
						groupIds: [groupId],
						itemId: null,
						specialProduct: {
							altCode: item.productId,
							description: item.title,
							price: item.unitPrice ?? 0,
							photoId: item.image?.photoId ?? undefined,
							specSheetId: item.specSheet?.specSheetId ?? undefined,
							specSheetName: item.specSheet?.specSheetName ?? undefined,
							manufacturer: item.manufacturer ?? undefined,
							finish: item.finish ?? undefined
						},
						quantity,
						ordinal: ordinal ?? undefined
					}
				},
				refetchQueries: [{ query: PROJECT_DETAILS_PAGE, variables: { projectId } }]
			});
		},
		updateProductOptions: async (productInfo: ProductUpdatePayload, newQuantity: number, newFinishId: number) => {
			const { itemId, parentItemId, cartId, groupId } = productInfo;
			const subItemId = parentItemId ? itemId : undefined;

			await changeCartItemFinish({
				variables: {
					input: {
						projectId,
						cartId,
						itemId: parentItemId ?? itemId,
						subItemId,
						quantity: newQuantity,
						productVariantId: newFinishId,
						groupId,
						isSingleCartProject: Boolean(project?.cartId)
					}
				}
			});
		},
		batchAddProductsToProject: (
			groupId: number,
			items: RemovedProjectItem[]
		): Promise<ApolloClient.MutateResult<AddProductsToProjectsMutation>> => {
			const cartId = groups.find((group) => group.id === groupId)?.cartId;
			return addProductsToProjectsBatch({
				variables: {
					input: {
						projects: [
							{
								cartId,
								projectId,
								quantity: 1,
								groupId: project?.cartId ? groupId : cartId
							}
						],
						items: items.map(({ item, index }) => ({
							product: {
								variantId: item.productVariantId,
								options: item.options?.map(({ id }) => ({ id })) ?? []
							},
							quantity: item.quantityUnPurchased || 1,
							ordinal: index + 1,
							subItems:
								'subItems' in item && item.subItems
									? item.subItems.map((subItem) => ({
											product: {
												variantId: subItem.productVariantId,
												options: subItem.options ?? []
											},
											quantity: subItem.quantityUnPurchased || 1
									  }))
									: []
						}))
					}
				},
				refetchQueries: [{ query: PROJECT_DETAILS_PAGE, variables: { projectId } }],
				awaitRefetchQueries: true
			});
		}
	};
};
